import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AppModule } from '../../app.module';
import { Globals } from '../../shared/functions';
import { Incident, IncidentCurrentState, INCIDENT_TIMELINE_STATES } from '../../models/incident';
import { IncidentService } from '../../shared/incident.service';
import { FleetBreakdownNavigationService, FleetBreakdownReturnContext } from '../../shared/fleet-breakdown-navigation.service';

@Component({
  selector: 'app-fleet-breakdown-list',
  imports: [AppModule, RouterLink],
  templateUrl: './fleet-breakdown-list.html',
  styleUrl: './fleet-breakdown-list.less'
})
export class FleetBreakdownList implements OnInit {
  public isCvvValid = true;
  public isLoading = false;
  public loadFailed = false;
  public incidents: Incident[] = [];
  public returnContext: FleetBreakdownReturnContext | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private incidentService: IncidentService,
    private fleetBreakdownNavigation: FleetBreakdownNavigationService
  ) {}

  ngOnInit(): void {
    this.returnContext = this.fleetBreakdownNavigation.getReturnContext();

    this.route.params.subscribe(params => {
      const fleetId = Number(params['fleetId']);

      this.isCvvValid = Number.isInteger(fleetId)
        && fleetId > 0
        && Globals.Functions.isFleetCvvValid(fleetId, 0, params['cvv']);

      if (this.isCvvValid) {
        this.loadIncidents(fleetId);
      }
    });
  }

  private loadIncidents(fleetId: number): void {
    this.isLoading = true;
    this.loadFailed = false;
    this.incidents = [];

    this.incidentService.getFleetIncidentsNoToken(fleetId).subscribe({
      next: incidents => {
        this.incidents = incidents;
        this.isLoading = false;
      },
      error: () => {
        this.loadFailed = true;
        this.isLoading = false;
      }
    });
  }

  public getIncidentCurrentState(incident: Incident): IncidentCurrentState | null {
    if (incident.incidentActions?.dtCompleted) {
      return null;
    }

    const now = new Date().getTime();
    let latestStepIndex = -1;
    let latestTime = -Infinity;
    let latestRawDate: any = null;

    for (let index = 0; index < INCIDENT_TIMELINE_STATES.length; index++) {
      const step = INCIDENT_TIMELINE_STATES[index];
      const source = step.refSource === 'incident' ? incident : incident.incidentActions;

      if (!source) {
        continue;
      }

      for (const field of step.completedFields) {
        const rawDate = (source as any)[field];
        if (!rawDate) {
          continue;
        }

        const timestamp = new Date(rawDate + 'Z').getTime();
        if (timestamp > latestTime) {
          latestTime = timestamp;
          latestStepIndex = index;
          latestRawDate = rawDate;
        }
      }
    }

    if (latestStepIndex === -1) {
      return null;
    }

    const step = INCIDENT_TIMELINE_STATES[latestStepIndex];
    const referenceTime = new Date(latestRawDate + 'Z');
    const minutesElapsed = Math.floor((now - referenceTime.getTime()) / 60000);

    if (step.etaBasedThresholds) {
      if (!incident.breakdownTechnicianDriverEta) {
        return { awaitingLabel: step.awaitingLabel, referenceTime, minutesElapsed, severity: 'normal' };
      }

      const etaTime = new Date(incident.breakdownTechnicianDriverEta + 'Z');
      const etaMinutesRemaining = Math.floor((etaTime.getTime() - now) / 60000);
      const minutesOverdue = -etaMinutesRemaining;
      const { overdueWarnMinutes, overdueCriticalMinutes } = step.etaBasedThresholds;
      const severity = minutesOverdue >= overdueCriticalMinutes ? 'critical'
        : minutesOverdue >= overdueWarnMinutes ? 'warn'
        : 'normal';

      return { awaitingLabel: step.awaitingLabel, referenceTime, minutesElapsed, severity, etaMinutesRemaining };
    }

    const severity = minutesElapsed >= step.criticalMinutes ? 'critical'
      : minutesElapsed >= step.warnMinutes ? 'warn'
      : 'normal';

    return { awaitingLabel: step.awaitingLabel, referenceTime, minutesElapsed, severity };
  }

  public returnToNewBreakdown(): void {
    if (!this.returnContext) {
      return;
    }

    this.router.navigate([
      '/new-breakdown',
      this.returnContext.fleetId,
      this.returnContext.cvv,
      this.returnContext.callcentreId
    ]);
  }
}
